import { useState } from "react";
import { api, apiErrorMessage } from "../../../lib/api";
import { useAdminMutation } from "../../../lib/adminData";
import { timezoneShiftConfirm } from "../../../lib/confirmCopy";
import { isoToWallClock, wallClockShiftMinutes, wallClockToIso } from "../../../lib/datetime";
import { runSequentially } from "../../../lib/sequence";
import { supportedZones, timezoneOptions } from "../../../lib/timezones";
import type { Bundle } from "../../../lib/types";
import { Button, Field, Input, SectionCard, Select } from "../../../components/ui";

/**
 * How the event is played and counted (console spec §5.2): the timezone every
 * game time is entered in, the game length and rest the conflict check uses,
 * the run-differential cap and the forfeit score the tiebreakers use. One
 * form, one Save.
 *
 * The hints matter: the engine's run cap feeds comparators only and nothing
 * ends a game, and a forfeit stores no score, so the labels must not suggest
 * otherwise.
 */
export function Rules({ tid, bundle }: { tid: string; bundle: Bundle }) {
  const t = bundle.tournament;
  const [timezone, setTimezone] = useState(t.timezone);
  const [gameLength, setGameLength] = useState(String(t.config.gameLengthMinutes));
  const [minRest, setMinRest] = useState(String(t.config.minRestMinutes));
  const [runCapEnabled, setRunCapEnabled] = useState(t.config.runCapEnabled);
  const [runCapMargin, setRunCapMargin] = useState(String(t.config.runCapMargin));
  const [winner, setWinner] = useState(String(t.config.forfeitScore.winner));
  const [loser, setLoser] = useState(String(t.config.forfeitScore.loser));
  const [shift, setShift] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const refresh = useAdminMutation(tid, () => Promise.resolve());

  const config = {
    ...t.config,
    gameLengthMinutes: Number(gameLength),
    minRestMinutes: Number(minRest),
    runCapEnabled,
    runCapMargin: Number(runCapMargin),
    forfeitScore: { winner: Number(winner), loser: Number(loser) },
  };
  const invalid =
    !(Number(gameLength) > 0) ||
    !(Number(minRest) >= 0) ||
    !(Number(runCapMargin) > 0) ||
    !(Number(winner) >= 0) ||
    !(Number(loser) >= 0);

  const scheduled = bundle.games.filter((g) => g.scheduledAt);

  const saveTournament = () =>
    api.put(`/admin/tournaments/${tid}`, { timezone, config }).then(
      () => {
        setError(undefined);
        setShift(null);
        refresh.mutate();
      },
      (e) => setError(apiErrorMessage(e, "Could not save the rules.")),
    );

  /**
   * Keep the clock times: every scheduled game is rewritten so 9:00 in the
   * old zone becomes 9:00 in the new one, then the tournament is saved.
   * Sequential, stopping at the first failure — the bundle refetches either
   * way, so the organizer sees exactly how far it got.
   */
  const keepClockTimes = async () => {
    const r = await runSequentially(
      scheduled.map((g) => () =>
        api.put(`/admin/tournaments/${tid}/games/${g.id}`, {
          scheduledAt: wallClockToIso(isoToWallClock(g.scheduledAt, t.timezone), timezone),
        }),
      ),
    );
    if (r.error) {
      setError(apiErrorMessage(r.error, `Could not move every game — ${r.done} of ${scheduled.length} were changed.`));
      refresh.mutate();
      return;
    }
    await saveTournament();
  };

  const submit = () => {
    if (invalid) return;
    if (timezone !== t.timezone && scheduled.length > 0) {
      const minutes = wallClockShiftMinutes(scheduled[0]!.scheduledAt!, t.timezone, timezone);
      const copy = timezoneShiftConfirm(scheduled.length, minutes);
      if (copy) {
        setShift(copy);
        return;
      }
    }
    void saveTournament();
  };

  const zones = timezoneOptions(t.timezone);

  return (
    <SectionCard title="Rules" description="How games are timed, checked for conflicts, and counted in the standings.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="grid max-w-3xl gap-4 sm:grid-cols-3">
          <Field label="Timezone" hint="Every game time on this event is entered and shown in this zone.">
            {supportedZones().length > 0 ? (
              <Select value={timezone} onChange={(e) => setTimezone(e.target.value)} aria-label="Timezone">
                {zones.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </Select>
            ) : (
              <Input value={timezone} onChange={(e) => setTimezone(e.target.value)} aria-label="Timezone" />
            )}
          </Field>
          <Field label="Game length (minutes)" hint="Used to spot a team booked twice at once.">
            <Input inputMode="numeric" value={gameLength} onChange={(e) => setGameLength(e.target.value)} aria-label="Game length" />
          </Field>
          <Field label="Rest between games (minutes)" hint="Flags a team asked to play again sooner than this.">
            <Input inputMode="numeric" value={minRest} onChange={(e) => setMinRest(e.target.value)} aria-label="Rest between games" />
          </Field>
        </div>
        <div className="grid max-w-3xl gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
              <input
                type="checkbox"
                checked={runCapEnabled}
                onChange={(e) => setRunCapEnabled(e.target.checked)}
                className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              Cap run differential in tiebreakers
            </label>
            <div className="mt-2 flex items-center gap-2">
              <Input
                inputMode="numeric"
                className="w-24"
                value={runCapMargin}
                onChange={(e) => setRunCapMargin(e.target.value)}
                disabled={!runCapEnabled}
                aria-label="Run differential cap"
              />
              <span className="text-xs text-slate-400">
                Tiebreakers only. A win by more than {runCapMargin || "the cap"} counts as {runCapMargin || "the cap"};
                scores are shown as entered.
              </span>
            </div>
          </div>
          <Field label="Forfeit score" hint="Counted in standings for a forfeit. Not shown as a score.">
            <div className="flex items-center gap-2">
              <Input inputMode="numeric" className="w-16 text-center" value={winner} onChange={(e) => setWinner(e.target.value)} aria-label="Forfeit score, winner" />
              <span className="text-slate-400">–</span>
              <Input inputMode="numeric" className="w-16 text-center" value={loser} onChange={(e) => setLoser(e.target.value)} aria-label="Forfeit score, loser" />
            </div>
          </Field>
        </div>
        {shift ? (
          <TimezoneShift
            text={shift}
            pending={refresh.isPending}
            onKeep={() => void keepClockTimes()}
            onShift={() => void saveTournament()}
            onCancel={() => setShift(null)}
          />
        ) : (
          <Button type="submit" disabled={invalid || refresh.isPending}>
            Save rules
          </Button>
        )}
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </form>
    </SectionCard>
  );
}

/**
 * The second step of a timezone change under scheduled games: what they would
 * read as if left alone, and the two ways forward. Rendered directly by the
 * tests, since it only appears after Save.
 */
export function TimezoneShift({
  text,
  pending,
  onKeep,
  onShift,
  onCancel,
}: {
  text: string;
  pending: boolean;
  onKeep: () => void;
  onShift: () => void;
  onCancel: () => void;
}) {
  return (
    <div role="region" aria-label="Before you change the timezone" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <p className="font-medium">{text}.</p>
      <p className="mt-1 text-amber-800">
        Keep the clock times to leave 9:00 as 9:00 in the new zone, or shift them to keep the same instants.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={onKeep} disabled={pending}>
          Keep the clock times
        </Button>
        <Button size="sm" variant="secondary" onClick={onShift} disabled={pending}>
          Shift them
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={pending}>
          Not yet
        </Button>
      </div>
    </div>
  );
}
